/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Snackbar,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Switch,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  AlternateEmail,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  ContentCopy,
  ExpandMore,
  Forward,
  InfoOutlined,
  MailOutline,
  PowerSettingsNew,
  Badge,
  WarningAmber,
  OpenInNew,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const ACCENT = "#0B996E";
const ACCENT_HOVER = "#087C59";
const ACCENT_SOFT = "rgba(11, 153, 110, 0.08)";
const SURFACE = "#F7F9F8";

const STEPS = [
  { id: "address", label: "Inbound address", icon: AlternateEmail },
  { id: "forwarding", label: "Forwarding", icon: Forward },
  { id: "display", label: "Display name", icon: Badge },
  { id: "enable", label: "Enable", icon: PowerSettingsNew },
];

const GMAIL_FORWARDING_URL = "https://mail.google.com/mail/u/0/#settings/fwdandpop";
const M365_ADMIN_URL = "https://admin.exchange.microsoft.com/";

function visibleFromAddress(supportEmail, inboundDomain, inboundAddress) {
  const support = (supportEmail || "").trim();
  const supportDomain = (support.split("@")[1] || "").trim().toLowerCase();
  const sendDomain = (inboundDomain || (inboundAddress || "").split("@")[1] || "")
    .trim()
    .toLowerCase();
  if (support && supportDomain && supportDomain === sendDomain) return support;
  return "your PearlCRM sender address";
}

function StatusChip({ enabled, configured, platformReady }) {
  if (!platformReady) {
    return (
      <Chip
        icon={<WarningAmber />}
        label="Platform email not ready"
        color="warning"
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  }
  if (enabled && configured) {
    return (
      <Chip
        icon={<CheckCircle />}
        label="Active"
        color="success"
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  }
  if (configured) {
    return (
      <Chip
        icon={<WarningAmber />}
        label="Ready · Disabled"
        color="warning"
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  }
  return <Chip label="Setup required" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
}

function StepProgress({ steps, activeStep, completedThrough, onStepClick }) {
  return (
    <MDBox
      display="flex"
      flexWrap="wrap"
      gap={1.25}
      mb={0}
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: SURFACE,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === activeStep;
        const isDone = index < completedThrough;
        const canClick = index <= Math.max(completedThrough, activeStep);

        return (
          <MDBox
            key={step.id}
            onClick={canClick ? () => onStepClick(index) : undefined}
            sx={{
              flex: "1 1 140px",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: 1.5,
              py: 1.25,
              borderRadius: 1.5,
              border: "1px solid",
              borderColor: isActive ? ACCENT : isDone ? "rgba(46, 125, 50, 0.35)" : "transparent",
              bgcolor: isActive ? "#fff" : isDone ? "rgba(46, 125, 50, 0.06)" : "transparent",
              cursor: canClick ? "pointer" : "default",
              opacity: canClick ? 1 : 0.5,
              boxShadow: isActive ? "0 1px 6px rgba(11, 153, 110, 0.12)" : "none",
              transition: "all 0.15s ease",
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                bgcolor: isActive ? ACCENT : isDone ? "success.main" : "grey.300",
                color: "#fff",
              }}
            >
              {isDone && !isActive ? (
                <CheckCircle sx={{ fontSize: 18 }} />
              ) : (
                <Icon sx={{ fontSize: 17 }} />
              )}
            </Box>
            <MDBox minWidth={0}>
              <MDTypography
                variant="caption"
                color="text"
                sx={{ display: "block", lineHeight: 1.2, opacity: 0.7 }}
              >
                Step {index + 1}
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight={isActive ? "bold" : "medium"}
                sx={{ display: "block", lineHeight: 1.25 }}
              >
                {step.label}
              </MDTypography>
            </MDBox>
          </MDBox>
        );
      })}
    </MDBox>
  );
}

function HowItWorks() {
  const stages = [
    { label: "Customer emails", detail: "support@yourcompany.com" },
    { label: "Your mail host", detail: "Forwards the message" },
    { label: "PearlCRM address", detail: "Unique for your org" },
    { label: "Case created", detail: "Status New, then the queue assigns it" },
  ];
  return (
    <Card
      variant="outlined"
      sx={{
        mb: 3,
        p: { xs: 2, md: 2.5 },
        borderRadius: 2,
        borderColor: "divider",
        bgcolor: "#fff",
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <MailOutline sx={{ color: ACCENT, fontSize: 22 }} />
        <MDTypography variant="button" fontWeight="bold">
          How Email-to-Case works
        </MDTypography>
      </MDBox>
      <MDBox
        display="flex"
        flexWrap="wrap"
        alignItems="stretch"
        gap={1}
        sx={{
          "& > .flow-arrow": {
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            color: "text.secondary",
            px: 0.5,
            fontWeight: 700,
          },
        }}
      >
        {stages.map((stage, i) => (
          <React.Fragment key={stage.label}>
            <MDBox
              flex="1 1 140px"
              sx={{
                p: 1.5,
                borderRadius: 1.5,
                bgcolor: SURFACE,
                border: "1px solid",
                borderColor: "divider",
                minWidth: 120,
              }}
            >
              <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ opacity: 0.55 }}>
                {i + 1}
              </MDTypography>
              <MDTypography variant="button" fontWeight="medium" display="block">
                {stage.label}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                {stage.detail}
              </MDTypography>
            </MDBox>
            {i < stages.length - 1 && <Box className="flow-arrow">→</Box>}
          </React.Fragment>
        ))}
      </MDBox>
      <MDTypography variant="caption" color="text" display="block" mt={2}>
        Customers keep writing to your public support address. You only add a forwarding rule — no
        Resend or Brevo account is required for your organisation.
      </MDTypography>
    </Card>
  );
}

function CopyableAddress({ value, onCopy, disabled }) {
  return (
    <MDBox
      sx={{
        display: "flex",
        alignItems: { xs: "stretch", sm: "center" },
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5,
        p: 2,
        borderRadius: 2,
        bgcolor: ACCENT_SOFT,
        border: "1px solid",
        borderColor: "rgba(11, 153, 110, 0.28)",
      }}
    >
      <MDBox flex={1} minWidth={0}>
        <MDTypography
          variant="caption"
          fontWeight="bold"
          sx={{ color: ACCENT, letterSpacing: 0.4, textTransform: "uppercase" }}
        >
          Your PearlCRM inbound address
        </MDTypography>
        <MDTypography
          variant="h6"
          fontWeight="medium"
          sx={{
            mt: 0.5,
            wordBreak: "break-all",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: { xs: "0.95rem", md: "1.05rem" },
          }}
        >
          {value || "Not generated yet"}
        </MDTypography>
      </MDBox>
      <MDButton
        variant="gradient"
        color="info"
        disabled={disabled || !value}
        onClick={() => onCopy(value, "Inbound address")}
        startIcon={<ContentCopy />}
        sx={{
          bgcolor: ACCENT,
          "&:hover": { bgcolor: ACCENT_HOVER },
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        Copy address
      </MDButton>
    </MDBox>
  );
}

function ForwardStepList({ steps }) {
  return (
    <Box
      component="ol"
      sx={{
        m: 0,
        pl: 2.5,
        "& li": { mb: 1.5, pl: 0.5 },
        "& li::marker": { color: ACCENT, fontWeight: 700 },
        "& code": {
          px: 0.75,
          py: 0.25,
          borderRadius: 1,
          bgcolor: "rgba(0,0,0,0.05)",
          fontSize: "0.85em",
          wordBreak: "break-all",
        },
        "& a": { color: ACCENT, fontWeight: 600 },
      }}
    >
      {steps.map((step, i) => (
        <Box
          component="li"
          key={i}
          sx={{
            typography: "body2",
            color: "text.primary",
            lineHeight: 1.6,
          }}
        >
          {step}
        </Box>
      ))}
    </Box>
  );
}

function ForwardingGuides({ inboundAddress, publicSupport }) {
  const [tab, setTab] = useState(0);
  const target = inboundAddress || "your PearlCRM inbound address";

  return (
    <Card
      variant="outlined"
      sx={{ borderRadius: 2, borderColor: "divider", overflow: "hidden", mb: 2 }}
    >
      <MDBox px={2} pt={1.5} bgcolor={SURFACE} borderBottom="1px solid" borderColor="divider">
        <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
          Forwarding instructions by provider
        </MDTypography>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 },
            "& .Mui-selected": { color: `${ACCENT} !important` },
            "& .MuiTabs-indicator": { bgcolor: ACCENT },
          }}
        >
          <Tab label="Google Workspace / Gmail" />
          <Tab label="Microsoft 365 / Outlook" />
          <Tab label="Other / cPanel" />
        </Tabs>
      </MDBox>

      <MDBox p={2.5}>
        {tab === 0 && (
          <>
            <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
              Use <strong>automatic forwarding</strong> (not filters that only “send a copy” without
              delivery to PearlCRM). Confirm the forwarding address when Gmail emails you a
              verification code.
            </Alert>
            <ForwardStepList
              steps={[
                <>
                  Open{" "}
                  <a href={GMAIL_FORWARDING_URL} target="_blank" rel="noreferrer">
                    Gmail forwarding settings
                  </a>{" "}
                  while signed in as the mailbox that receives {publicSupport} (or an admin who can
                  manage that user).
                </>,
                <>
                  Click <strong>Add a forwarding address</strong> and paste{" "}
                  <code>{target}</code>.
                </>,
                <>
                  Confirm the verification email if Google asks. Then choose{" "}
                  <strong>Forward a copy of incoming mail to</strong> that address. Keep a copy in
                  the inbox if you still want to read mail in Gmail.
                </>,
                <>
                  Save changes. Send a test message to {publicSupport} and check it arrives at{" "}
                  <code>{target}</code> (after Email-to-Case is enabled, a case should appear).
                </>,
              ]}
            />
            <MDButton
              variant="outlined"
              color="dark"
              size="small"
              href={GMAIL_FORWARDING_URL}
              target="_blank"
              rel="noreferrer"
              endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
              sx={{ mt: 1 }}
            >
              Open Gmail forwarding
            </MDButton>
          </>
        )}

        {tab === 1 && (
          <>
            <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
              Prefer an <strong>Exchange mail flow / transport rule</strong> or mailbox forwarding
              so the full message (including headers) is redirected. Do not use client-only Outlook
              rules on a single PC.
            </Alert>
            <ForwardStepList
              steps={[
                <>
                  Sign in to the{" "}
                  <a href={M365_ADMIN_URL} target="_blank" rel="noreferrer">
                    Exchange admin center
                  </a>{" "}
                  (or Microsoft 365 admin) with permission to manage {publicSupport}.
                </>,
                <>
                  Open the mailbox for {publicSupport} → <strong>Mailbox</strong> /{" "}
                  <strong>Email forwarding</strong>, <em>or</em> create a mail flow rule that
                  applies when the message is sent to that address.
                </>,
                <>
                  Enable forwarding / redirect to <code>{target}</code>. Keep a copy in the mailbox
                  if your team still works from Outlook.
                </>,
                <>
                  Allow a few minutes for the rule to apply, then send a test email to{" "}
                  {publicSupport}.
                </>,
              ]}
            />
            <MDButton
              variant="outlined"
              color="dark"
              size="small"
              href={M365_ADMIN_URL}
              target="_blank"
              rel="noreferrer"
              endIcon={<OpenInNew sx={{ fontSize: 16 }} />}
              sx={{ mt: 1 }}
            >
              Open Exchange admin
            </MDButton>
          </>
        )}

        {tab === 2 && (
          <>
            <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
              Most hosts call this <strong>Email forwarding</strong>, <strong>aliases</strong>, or{" "}
              <strong>redirect</strong>. Forward the whole message — not only a notification.
            </Alert>
            <ForwardStepList
              steps={[
                <>
                  In your DNS / hosting panel (cPanel, Google Domains mail, Zoho, Fastmail, etc.),
                  open the mailbox or alias for {publicSupport}.
                </>,
                <>
                  Add a forward / redirect destination of <code>{target}</code>.
                </>,
                <>
                  If the host offers “keep a copy”, leave it on so your team can still read mail in
                  the original inbox.
                </>,
                <>
                  Save, wait for propagation if needed, then email {publicSupport} from an external
                  address to test.
                </>,
              ]}
            />
          </>
        )}
      </MDBox>
    </Card>
  );
}

const STRATEGY_LABELS = {
  round_robin: "Round robin",
  longest_idle: "Longest idle",
  fewest_active: "Fewest active",
  least_active: "Least active",
  skills_then_longest_idle: "Skills, then longest idle",
  most_available: "Most available",
  manual: "Manual",
};

function strategyLabel(value) {
  return STRATEGY_LABELS[value] || value || "Longest idle";
}

function ChecklistItem({ done, children }) {
  return (
    <MDBox display="flex" alignItems="flex-start" gap={1.25} mb={1.25}>
      <CheckCircle
        sx={{
          fontSize: 20,
          mt: 0.15,
          color: done ? "success.main" : "grey.400",
        }}
      />
      <MDTypography variant="body2" color="text" sx={{ lineHeight: 1.5 }}>
        {children}
      </MDTypography>
    </MDBox>
  );
}

const CASE_EMAIL_TEMPLATES_PATH = "/settings/company-settings?tab=templates&category=case-email";

export default function EmailToCaseSettingsPage() {
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const isV2 = useTheme()?.pearlUi === "v2";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeStep, setActiveStep] = useState(0);

  const [settings, setSettings] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [supportEmail, setSupportEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [reopenOnReply, setReopenOnReply] = useState(true);
  const [queues, setQueues] = useState([]);
  const [queueId, setQueueId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/email-to-case/settings");
      const data = res.data || {};
      setSettings(data);
      setEnabled(Boolean(data.enabled));
      setSupportEmail(data.support_email || "");
      setDisplayName(data.support_display_name || "");
      setReopenOnReply(data.reopen_on_reply !== false);
      setQueueId(data.default_queue_id ? String(data.default_queue_id) : "");
      try {
        const queuesRes = await axiosInstance.get("/call-center/queues");
        const rows = Array.isArray(queuesRes.data) ? queuesRes.data : [];
        setQueues(
          rows.filter(
            (queue) =>
              queue.is_active !== false &&
              (queue.object_api_names || []).includes("cases")
          )
        );
      } catch {
        setQueues([]);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Unable to load Email-to-Case settings."
      );
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const inboundAddress = (settings?.inbound_address || "").trim();
  const addressReady = Boolean(inboundAddress);
  const platformReady = Boolean(settings?.platform_email_ready);
  const configured = Boolean(settings?.configured);
  const displayReady = Boolean(displayName.trim() || supportEmail.trim());

  const completedThrough = (() => {
    if (addressReady && configured && enabled) return 4;
    if (addressReady && displayReady) return 3;
    if (addressReady && supportEmail.trim()) return 2;
    if (addressReady) return 1;
    return 0;
  })();

  const copyValue = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setSuccess(`${label} copied to clipboard.`);
    } catch {
      setError(`Could not copy ${label.toLowerCase()}.`);
    }
  };

  const buildPayload = (extra = {}) => ({
    enabled,
    support_email: supportEmail || null,
    support_display_name: displayName || null,
    reopen_on_reply: reopenOnReply,
    default_queue_id: queueId ? Number(queueId) : null,
    ...extra,
  });

  const handleSave = async (extra = {}, message = "Email-to-Case settings saved.") => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await axiosInstance.put("/email-to-case/settings", buildPayload(extra));
      setSuccess(message);
      await load();
      return true;
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Unable to save Email-to-Case settings."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = async () => {
    if (activeStep === 0 && !addressReady) {
      setError(
        "Inbound address is not available yet. Ask PearlCRM ops to set EMAIL_TO_CASE_INBOUND_DOMAIN, then reload this page."
      );
      return;
    }
    // Persist display fields when leaving that step
    if (activeStep === 2) {
      const ok = await handleSave({ enabled }, "Display settings saved.");
      if (!ok) return;
    }
    setError("");
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setError("");
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const publicSupport = supportEmail.trim() || "support@yourcompany.com";
  const selectedQueue = queues.find((queue) => String(queue.id) === String(queueId)) || null;

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
              Your unique inbound address
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={2.5}>
              PearlCRM created this address for your organisation. Copy it — you will paste it as
              the forwarding destination in the next step.
            </MDTypography>

            {!platformReady && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Platform email is not fully configured yet. You can still copy the address once it
                appears, but enabling Email-to-Case requires PearlCRM ops to finish Resend setup.
              </Alert>
            )}

            <CopyableAddress
              value={inboundAddress}
              onCopy={copyValue}
              disabled={!inboundAddress}
            />

            {settings?.inbound_domain && (
              <MDTypography variant="caption" color="text" display="block" mt={1.5}>
                Domain: <strong>{settings.inbound_domain}</strong>
                {settings?.tenant_slug ? (
                  <>
                    {" "}
                    · Tenant: <strong>{settings.tenant_slug}</strong>
                  </>
                ) : null}
              </MDTypography>
            )}

            <Accordion
              disableGutters
              elevation={0}
              sx={{
                mt: 2.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "8px !important",
                "&:before": { display: "none" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <MDTypography variant="button" fontWeight="medium">
                  Why does this address look unusual?
                </MDTypography>
              </AccordionSummary>
              <AccordionDetails>
                <MDTypography variant="body2" color="text" mb={1.5}>
                  The format is <code>{"{your-org}+{private-code}@pearlcrm.com.au"}</code> (or{" "}
                  <code>@pearlcrm.com</code> in production). The code uniquely identifies your
                  tenant so mail is never mixed with another company.
                </MDTypography>
                <MDTypography variant="body2" color="text">
                  Rotating the address creates a new code. Update your mail forwarding rule
                  afterward, or new mail will not create cases.
                </MDTypography>
                <MDButton
                  variant="text"
                  color="error"
                  size="small"
                  disabled={saving || !inboundAddress}
                  onClick={() =>
                    handleSave(
                      { rotate_inbound_token: true, enabled: false },
                      "New inbound address generated. Update your forwarding rule to the new address."
                    )
                  }
                  sx={{ mt: 1, ml: -1 }}
                >
                  Rotate inbound address
                </MDButton>
              </AccordionDetails>
            </Accordion>
          </>
        );

      case 1:
        return (
          <>
            <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
              Forward your public support inbox
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={2}>
              Customers keep emailing <strong>{publicSupport}</strong>. Your mail host forwards each
              message to PearlCRM. Pick your provider below and follow the steps.
            </MDTypography>

            <MDBox
              sx={{
                mb: 2.5,
                p: 2,
                borderRadius: 2,
                bgcolor: SURFACE,
                border: "1px dashed",
                borderColor: "divider",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MDTypography variant="button" fontWeight="medium">
                Forward to:
              </MDTypography>
              <code style={{ wordBreak: "break-all" }}>{inboundAddress || "—"}</code>
              <Tooltip title="Copy">
                <span>
                  <IconButton
                    size="small"
                    disabled={!inboundAddress}
                    onClick={() => copyValue(inboundAddress, "Inbound address")}
                  >
                    <ContentCopy sx={{ fontSize: 18 }} />
                  </IconButton>
                </span>
              </Tooltip>
            </MDBox>

            <ForwardingGuides inboundAddress={inboundAddress} publicSupport={publicSupport} />

            <TextField
              fullWidth
              label="Your public support email"
              value={supportEmail}
              onChange={(e) => setSupportEmail(e.target.value)}
              helperText="The address customers already use (e.g. support@yourcompany.com). Also used as Reply-To on agent replies."
              placeholder="support@yourcompany.com"
              sx={{ maxWidth: 520, mt: 1 }}
            />
          </>
        );

      case 2:
        return (
          <>
            <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
              How replies look to customers
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={2.5}>
              Customers see your public support email, not the generated inbound address. That
              generated address only receives mail you forward into PearlCRM. If your support
              email uses a different domain than PearlCRM, the message is sent from the PearlCRM
              sender address and Reply-To is your support email.
            </MDTypography>

            <GridFields>
              <TextField
                fullWidth
                label="Support display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                helperText="Shown as the From name, e.g. “Acme Support”."
                placeholder="Acme Support"
              />
              <TextField
                fullWidth
                label="Reply-To email"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                helperText="The address customers see and reply to. Forward that mailbox to the inbound address so replies still open on the case."
                placeholder="support@yourcompany.com"
              />
            </GridFields>

            <Card
              variant="outlined"
              sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: SURFACE, borderColor: "divider" }}
            >
              <MDTypography variant="caption" fontWeight="bold" color="text" sx={{ opacity: 0.6 }}>
                Preview
              </MDTypography>
              <MDTypography variant="body2" mt={0.5}>
                <strong>From:</strong>{" "}
                {displayName.trim() || "Support"} &lt;{visibleFromAddress(supportEmail, settings?.inbound_domain, inboundAddress)}&gt;
              </MDTypography>
              <MDTypography variant="body2">
                <strong>Reply-To:</strong> {supportEmail.trim() || "—"}
              </MDTypography>
            </Card>

            <FormControlLabel
              sx={{ mt: 2, display: "flex", alignItems: "flex-start", ml: 0 }}
              control={
                <Switch
                  checked={reopenOnReply}
                  onChange={(e) => setReopenOnReply(e.target.checked)}
                  sx={{
                    mt: 0.25,
                    "& .MuiSwitch-switchBase.Mui-checked": { color: ACCENT },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      bgcolor: ACCENT,
                    },
                  }}
                />
              }
              label={
                <MDBox>
                  <MDTypography variant="button" fontWeight="medium" display="block">
                    Reopen closed cases when the customer replies
                  </MDTypography>
                  <MDTypography variant="caption" color="text">
                    If a case is Closed / Resolved and the customer emails again on the same thread,
                    status returns to Open.
                  </MDTypography>
                </MDBox>
              }
            />
          </>
        );

      case 3:
        return (
          <>
            <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
              Enable Email-to-Case
            </MDTypography>
            <MDTypography variant="body2" color="text" mb={2.5}>
              Confirm the checklist, choose the queue that should receive new email cases, then
              turn the feature on. New cases are created with status New.
            </MDTypography>

            <TextField
              select
              required
              fullWidth
              label="Assignment queue"
              value={queueId}
              onChange={(e) => setQueueId(e.target.value)}
              error={!queueId}
              helperText={
                queues.length === 0
                  ? "No case queue exists yet. Create one under Queues that handles Cases, then choose it here."
                  : "Required. Only queues set up to handle cases are listed. New email cases go to a member of this queue."
              }
              sx={{ maxWidth: 640, mb: 2 }}
            >
              <MenuItem value="" sx={{ display: "none" }} />
              {queues.map((queue) => (
                <MenuItem key={queue.id} value={String(queue.id)}>
                  {queue.name} · {strategyLabel(queue.routing_strategy)} · {queue.routing_mode}
                </MenuItem>
              ))}
            </TextField>

            {selectedQueue && selectedQueue.routing_mode === "pull" && (
              <Alert severity="warning" sx={{ mb: 2, maxWidth: 640 }}>
                This queue is Pull, so Round robin and Longest idle will not assign automatically.
                The case stays in the queue until a member claims it or someone assigns it. Set the
                queue to Push or Hybrid for automatic assignment.
              </Alert>
            )}

            {queueId && queues.length > 0 && !selectedQueue && (
              <Alert severity="warning" sx={{ mb: 2, maxWidth: 640 }}>
                The saved queue is not a case queue. Choose a queue that handles cases before you finish.
              </Alert>
            )}

            <Card
              variant="outlined"
              sx={{ p: 2.5, mb: 2.5, borderRadius: 2, borderColor: "divider" }}
            >
              <MDTypography variant="button" fontWeight="bold" display="block" mb={1.5}>
                Checklist
              </MDTypography>
              <ChecklistItem done={addressReady}>
                Inbound address copied: <code>{inboundAddress || "—"}</code>
              </ChecklistItem>
              <ChecklistItem done={Boolean(supportEmail.trim())}>
                Forwarding set from <strong>{publicSupport}</strong> → PearlCRM address
              </ChecklistItem>
              <ChecklistItem done={Boolean(displayName.trim())}>
                Display name set{displayName.trim() ? `: “${displayName.trim()}”` : ""}
              </ChecklistItem>
              <ChecklistItem done={platformReady && configured}>
                Platform email ready
              </ChecklistItem>
              <ChecklistItem done={Boolean(queueId)}>
                Assignment queue
                {selectedQueue
                  ? `: ${selectedQueue.name} (${strategyLabel(selectedQueue.routing_strategy)})`
                  : ""}
              </ChecklistItem>
            </Card>

            {!platformReady && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Platform Resend is not ready. Contact PearlCRM support before enabling.
              </Alert>
            )}

            <Card
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: 2,
                borderColor: enabled ? "rgba(11, 153, 110, 0.4)" : "divider",
                bgcolor: enabled ? ACCENT_SOFT : "#fff",
              }}
            >
              <FormControlLabel
                sx={{ m: 0, alignItems: "flex-start", width: "100%" }}
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    disabled={!addressReady}
                    sx={{
                      mt: 0.25,
                      "& .MuiSwitch-switchBase.Mui-checked": { color: ACCENT },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: ACCENT,
                      },
                    }}
                  />
                }
                label={
                  <MDBox>
                    <MDTypography variant="h6" fontWeight="medium">
                      {enabled ? "Email-to-Case is on" : "Email-to-Case is off"}
                    </MDTypography>
                    <MDTypography variant="body2" color="text">
                      {enabled
                        ? "New mail forwarded to your inbound address will create or update cases."
                        : "Inbound mail is ignored until you enable and save."}
                    </MDTypography>
                  </MDBox>
                }
              />
            </Card>
          </>
        );

      default:
        return null;
    }
  };

  const summary =
    "Turn support email into cases automatically. Forward your public inbox to a PearlCRM address — no email provider API keys for your team.";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 3 : 3} pb={6} px={isV2 ? 0 : 2}>
        {isV2 ? (
          <PageHeader
            title="Email to Case"
            summary={summary}
            actions={
              !loading ? (
                <StatusChip
                  enabled={enabled}
                  configured={configured}
                  platformReady={platformReady}
                />
              ) : null
            }
          />
        ) : (
          <MDBox
            display="flex"
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            flexWrap="wrap"
            gap={2}
            mb={3}
          >
            <MDBox display="flex" alignItems="flex-start" gap={1}>
              <IconButton onClick={() => navigate("/settings")} sx={{ mt: 0.25 }}>
                <ArrowBack />
              </IconButton>
              <MDBox>
                <MDBox display="flex" alignItems="center" flexWrap="wrap" gap={1.25} mb={0.5}>
                  <MDTypography variant="h4" fontWeight="medium">
                    Email to Case
                  </MDTypography>
                  {!loading && (
                    <StatusChip
                      enabled={enabled}
                      configured={configured}
                      platformReady={platformReady}
                    />
                  )}
                </MDBox>
                <MDTypography variant="body2" color="text" sx={{ maxWidth: 560 }}>
                  {summary}
                </MDTypography>
              </MDBox>
            </MDBox>
          </MDBox>
        )}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            <HowItWorks />

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                {typeof error === "string" ? error : JSON.stringify(error)}
              </Alert>
            )}

            <Card
              sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 2,
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              }}
            >
              <StepProgress
                steps={STEPS}
                activeStep={activeStep}
                completedThrough={Math.max(completedThrough, activeStep)}
                onStepClick={setActiveStep}
              />

              <Divider sx={{ my: 3 }} />
              {renderStepContent()}
              <Divider sx={{ my: 3 }} />

              <MDBox display="flex" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
                <MDButton
                  variant="outlined"
                  color="dark"
                  disabled={activeStep === 0 || saving}
                  onClick={goBack}
                  startIcon={<ArrowBack />}
                >
                  Back
                </MDButton>
                {activeStep < STEPS.length - 1 ? (
                  <MDButton
                    variant="gradient"
                    color="info"
                    disabled={saving}
                    onClick={goNext}
                    endIcon={<ArrowForward />}
                    sx={{ bgcolor: ACCENT, "&:hover": { bgcolor: ACCENT_HOVER } }}
                  >
                    Continue
                  </MDButton>
                ) : (
                  <MDButton
                    variant="gradient"
                    color="info"
                    disabled={saving || !addressReady || !queueId}
                    onClick={() => {
                      if (!queueId) {
                        setError("Choose the case queue that should receive new email cases.");
                        return;
                      }
                      handleSave(
                        {},
                        enabled
                          ? `Email-to-Case is on. Send a test message to ${publicSupport}, then check Cases.`
                          : "Email-to-Case settings saved."
                      );
                    }}
                    sx={{ bgcolor: ACCENT, "&:hover": { bgcolor: ACCENT_HOVER } }}
                  >
                    {saving ? "Saving…" : "Save & finish"}
                  </MDButton>
                )}
              </MDBox>
            </Card>

            <Card
              sx={{
                p: { xs: 2, md: 3 },
                mt: 3,
                borderRadius: 2,
                boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              }}
            >
              <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
                Customer email templates
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Set up the emails customers receive for a case in{" "}
                <MDBox
                  component="button"
                  type="button"
                  onClick={() => navigate(CASE_EMAIL_TEMPLATES_PATH)}
                  sx={{
                    border: 0,
                    p: 0,
                    background: "none",
                    color: ACCENT,
                    font: "inherit",
                    fontWeight: 600,
                    cursor: "pointer",
                    textDecoration: "underline",
                  }}
                >
                  Company Settings → Templates → Case email
                </MDBox>
                .
              </MDTypography>
            </Card>
          </>
        )}
        <Snackbar
          open={Boolean(success)}
          autoHideDuration={5000}
          onClose={() => setSuccess("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert severity="success" onClose={() => setSuccess("")} sx={{ width: "100%" }}>
            {success}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}

function GridFields({ children }) {
  return (
    <MDBox
      display="grid"
      gap={2}
      sx={{
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        maxWidth: 900,
      }}
    >
      {children}
    </MDBox>
  );
}
